/**
 * AS3-faithful mage experience and level calculations.
 * Source: com/giab/games/gcl/gs/util/Calculator.as.
 */

/**
 * Returns the minimum XP required to reach `level`.
 * playerLevelReq(0) = 0, playerLevelReq(1) = 200, etc.
 * Builds the table up to `level` on each call (pure function, no global state).
 */
export function playerLevelReq(level: number): number {
  if (level === 0) return 0;
  // Build table up to level (index = level, value = XP required).
  let prev = 200;
  let step = 200;
  // table[i] = XP required for level i (i >= 1)
  let result = 0;
  for (let i = 1; i <= level; i++) {
    result = prev;
    prev += step;
    step += 100;
    if (i % 3 === 0) step += 100;
    if (i % 5 === 0) step += 100;
    if (i % 11 === 0) step += 100;
  }
  return result;
}

/**
 * Returns the player level for a given total XP.
 * level 0 = [0, 200), level 1 = [200, 400), etc.
 * AS3: increment level until req > xp, then return level-1.
 */
export function levelFromXp(xp: number): number {
  let lvl = 0;
  // AS3 loop: while req(lvl+1) <= xp → lvl++
  // Equivalent: keep incrementing until req exceeds xp.
  while (true) {
    const req = playerLevelReq(lvl + 1);
    if (req > xp) break;
    lvl++;
  }
  return Math.max(0, lvl);
}

/**
 * Returns XP needed to advance from current level to the next.
 */
export function xpToNextLevel(xp: number): number {
  const lvl = levelFromXp(xp);
  return playerLevelReq(lvl + 1) - xp;
}

/**
 * Calculates base XP reward for completing a field.
 * AS3: Calculator.calculateStageBaseXp(stageData)
 */
export function stageBaseXp(wavesNum: number, monsterBaseHp: number): number {
  return Math.round(
    Math.pow(
      100 * Math.pow(wavesNum * 18.35, 0.98) * Math.pow(monsterBaseHp * 19.63, 0.98),
      0.425
    )
  );
}
